<!-- Two items side by side with their date and note (“View together”). Click a picture to zoom it. -->
<template>
  <section class="md-compare" :class="`is-${layout}`" aria-label="Compare media">
    <header class="md-compare-head">
      <button v-if="backLabel" class="md-back" @click="$emit('back')">‹ {{ backLabel }}</button>
      <h2 class="md-grow">Compare media</h2>
      <button v-if="ordered.length === 2" class="md-btn ghost sm" @click="flipped = !flipped">Swap sides</button>
    </header>
    <p v-if="loading" class="md-compare-msg">Loading…</p>
    <p v-else-if="ordered.length !== 2" class="md-compare-msg">{{ error || 'These two items are not available any more.' }}</p>
    <div v-else class="md-compare-panes">
      <figure v-for="(it, i) in ordered" :key="it.id" class="md-compare-pane">
        <div class="md-compare-stage" :class="{ zoom: zoomed[i] }" @click="zoomed.splice(i, 1, !zoomed[i])">
          <MediaFull :item="it" :contain="!zoomed[i]" />
          <span class="md-compare-zoom">{{ zoomed[i] ? '−' : '+' }}</span>
        </div>
        <figcaption>
          <strong>{{ fmt(it.captured_at) }}</strong>
          <span v-if="gap && i === 1" class="md-compare-gap md-small"> · {{ gap }} later</span>
          <p>{{ it.note || 'No note.' }}</p>
          <span class="md-small md-compare-by">By {{ it.author_name }}</span>
        </figcaption>
      </figure>
    </div>
  </section>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import { getMediaItem } from '@/api/mediaApi';
import MediaFull from './MediaFull.vue';

const props = defineProps({
  ids: { type: Array, required: true },
  layout: { type: String, default: 'page' },
  backLabel: { type: String, default: '' },
});
const emit = defineEmits(['back']);
const items = ref([]);
const loading = ref(true);
const error = ref('');
// Older first, so the pair reads left to right in time.
const flipped = ref(false);
const zoomed = ref([false, false]);
const ordered = computed(() => {
  const pair = [...items.value].sort((a, b) => a.captured_at.localeCompare(b.captured_at));
  return flipped.value ? pair.reverse() : pair;
});
const gap = computed(() => {
  const [a, b] = [...items.value].sort((x, y) => x.captured_at.localeCompare(y.captured_at));
  const days = Math.round((new Date(b.captured_at) - new Date(a.captured_at)) / 864e5);
  if (days <= 0) return '';
  return days === 1 ? '1 day' : `${days} days`;
});

async function load() {
  loading.value = true;
  error.value = '';
  try { items.value = await Promise.all(props.ids.map((id) => getMediaItem(id))); } catch (e) { items.value = []; error.value = e.message; }
  loading.value = false;
}
function onKey(e) { if (e.key === 'Escape' && !document.querySelector('.swal2-container')) emit('back'); }
const fmt = (iso) => new Date(iso).toLocaleString([], { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });

watch(() => props.ids.join(','), load);
onMounted(() => { load(); window.addEventListener('keydown', onKey); });
onBeforeUnmount(() => window.removeEventListener('keydown', onKey));
</script>

<style scoped>
.md-compare-head { display:flex; align-items:center; gap:12px; flex-wrap:wrap; padding-bottom:12px; }
.md-compare-head .md-back { flex-basis:100%; }
.md-compare-head h2 { font-size:22px; }
.md-compare-msg { color:var(--muted); padding:24px 0; }
.md-compare-panes { display:grid; grid-template-columns:1fr 1fr; gap:14px; }
.md-compare-pane { margin:0; min-width:0; }
.md-compare-stage { position:relative; height:min(60vh, 560px); border-radius:10px; overflow:hidden; cursor:zoom-in; }
.md-compare-stage.zoom { cursor:zoom-out; overflow:auto; }
.md-compare-stage.zoom :deep(img) { width:200%; max-width:none; height:auto; max-height:none; }
.md-compare-zoom { position:absolute; top:8px; right:8px; width:30px; height:30px; border-radius:50%; background:rgba(255,255,255,.85); color:var(--ink); display:flex; align-items:center; justify-content:center; font-weight:700; }
.md-compare-gap, .md-compare-by { color:var(--muted); }
figcaption { padding:10px 2px; }
figcaption p { margin:4px 0; }
/* The split view's panel is narrow: stack the pair. */
.md-compare.is-panel .md-compare-panes { grid-template-columns:1fr; }
.md-compare.is-panel .md-compare-stage { height:min(40vh, 380px); }
.md-compare.is-immersive { color:#e8eef0; height:100%; overflow:auto; padding:12px 16px; }
.md-compare.is-immersive .md-back { color:#fff; flex-basis:auto; }
.md-compare.is-immersive .md-btn.ghost { background:transparent; color:#fff; border-color:rgba(255,255,255,.5); }
.md-compare.is-immersive .md-compare-stage { height:calc(100vh - 220px); min-height:240px; }
.md-compare.is-immersive .md-compare-gap, .md-compare.is-immersive .md-compare-by { color:#b8c4c8; }
@media (max-width: 640px) {
  .md-compare-panes { grid-template-columns:1fr; }
  .md-compare-stage, .md-compare.is-immersive .md-compare-stage { height:36vh; }
}
</style>
